import { useState } from "react";

// 專案時間:單日 / 範圍(業主 2026-09-30):
//   > 報價單調整 只留下 專案時間 / 專案起日/專案迄日 移除 / 專案時間 可以選擇範圍/單日 並且規格化
//
// 業主選的:只要日期(不填時段)。格式 2026-09-30 晚上改成 2026/12/12(Sat)-12/13(Sun)
// (「統一規則化為 2026/09/30(Wed)」,範圍省略年份,跨年才補)。
//
// **印出來的那段字是後端從日期產生的**(`backend/app/event_period.py`),這裡只是預告 ——
// 兩邊的格式要一樣,`formatPeriod` 與那一支是同一套規則。
// 日期沒動就預告原本存著的字:舊單以前手打的「12/12 下午 – 12/13 全日」存檔後也不會變。

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parse(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function day(iso: string, withYear: boolean): string {
  const at = parse(iso);
  const head = withYear ? `${at.getUTCFullYear()}/` : "";
  const mm = String(at.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(at.getUTCDate()).padStart(2, "0");
  return `${head}${mm}/${dd}(${WEEKDAYS[at.getUTCDay()]})`;
}

/** 起訖日 → 印給客戶看的那一段字。與後端 `format_period` 同一套規則。 */
export function formatPeriod(start: string | null, end: string | null): string {
  const first = start || end;
  const last = end || start;
  if (!first || !last) return "";
  if (first === last) return day(first, true);
  return `${day(first, true)}-${day(last, first.slice(0, 4) !== last.slice(0, 4))}`;
}

export interface EventPeriodFieldProps {
  start: string | null;
  end: string | null;
  /** 存著的那段字(舊單可能是以前手打的)。 */
  storedText: string;
  /** 打開這張單時的日期 —— 沒改日期就照印存著的字。 */
  originalStart: string | null;
  originalEnd: string | null;
  onChange: (start: string | null, end: string | null) => void;
  disabled?: boolean;
}

export function EventPeriodField({
  start,
  end,
  storedText,
  originalStart,
  originalEnd,
  onChange,
  disabled,
}: EventPeriodFieldProps) {
  const [range, setRange] = useState(() => Boolean(start && end && start !== end));
  const touched = start !== originalStart || end !== originalEnd;
  const printed = touched ? formatPeriod(start, end) : storedText || formatPeriod(start, end);
  const legacyOnly = !start && !end && storedText.trim() !== "";

  const inputClass =
    "h-9 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2 text-sm text-[var(--text)]";

  return (
    <div role="group" aria-label="專案時間" className="flex min-w-0 flex-col gap-1 py-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-[var(--text-3)]">專案時間</span>
        <span className="flex items-center gap-3 text-xs text-[var(--text-2)]">
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              name="event-period-mode"
              checked={!range}
              disabled={disabled}
              onChange={() => {
                setRange(false);
                onChange(start, start);
              }}
            />
            單日
          </label>
          <label className="inline-flex items-center gap-1">
            <input
              type="radio"
              name="event-period-mode"
              checked={range}
              disabled={disabled}
              onChange={() => setRange(true)}
            />
            範圍
          </label>
        </span>
      </div>
      {range ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            type="date"
            aria-label="開始日期"
            className={inputClass}
            value={start ?? ""}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value || null, end)}
          />
          <span className="text-[var(--text-3)]">–</span>
          <input
            type="date"
            aria-label="結束日期"
            className={inputClass}
            value={end ?? ""}
            min={start ?? undefined}
            disabled={disabled}
            onChange={(event) => onChange(start, event.target.value || null)}
          />
        </div>
      ) : (
        <input
          type="date"
          aria-label="日期"
          className={inputClass}
          value={start ?? ""}
          disabled={disabled}
          onChange={(event) => {
            const picked = event.target.value || null;
            onChange(picked, picked);
          }}
        />
      )}
      {legacyOnly ? (
        <span className="text-xs text-[var(--warn)]">
          原本寫的:{storedText}(選好日期後會換成新格式)
        </span>
      ) : printed ? (
        <span className="text-xs text-[var(--text-3)]">印在報價單上:{printed}</span>
      ) : (
        <span className="text-xs text-[var(--text-3)]">還沒選日期(選了才會出現在日曆上)</span>
      )}
    </div>
  );
}
